import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const ConnectRefresh = () => {
  const [searchParams] = useSearchParams();
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [status, setStatus] = useState("Preparing a fresh onboarding link…");
  const [error, setError] = useState("");

  const uid = useMemo(() => (searchParams.get("uid") || "").trim(), [searchParams]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1024px)");
    const apply = () => setIsSmallScreen(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!BACKEND) {
        setError("Backend URL is not configured.");
        setStatus("");
        return;
      }

      if (!uid) {
        setError("Missing onboarding reference.");
        setStatus("");
        return;
      }

      try {
        setError("");
        setStatus("Preparing a fresh onboarding link…");

        const res = await fetch(`${BACKEND}/connect/account-link`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ influencer_uid: uid }),
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok || !data?.url) {
          throw new Error(data?.error || `HTTP ${res.status}`);
        }

        if (cancelled) return;

        setStatus("Redirecting you to Stripe onboarding…");
        window.location.replace(data.url);
      } catch (err) {
        if (cancelled) return;
        setStatus("");
        setError(
          err?.message
            ? `We could not refresh your onboarding link (${err.message}).`
            : "We could not refresh your onboarding link."
        );
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [uid]);

  return (
    <div style={{ ...styles.outerWrap, ...(isSmallScreen ? styles.outerWrapCompact : null) }}>
      <div style={{ ...styles.card, ...(isSmallScreen ? styles.cardWide : null) }}>
        <h2 style={styles.title}>Refreshing Stripe Onboarding</h2>

        {!!status && <p style={styles.text}>{status}</p>}

        {!!error && (
          <>
            <p style={{ ...styles.text, color: "#b91c1c" }}>{error}</p>
            <p style={styles.subtle}>
              Please return to AskLawly and try again. If the problem continues, contact support.
            </p>
            <div style={styles.buttonRow}>
              <a href="/home" style={styles.primaryButton}>
                Back to Home
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  outerWrap: {
    maxWidth: 960,
    margin: "24px auto",
    padding: "0 16px 40px",
  },
  outerWrapCompact: {
    padding: "0 8px 40px",
  },
  card: {
    maxWidth: 520,
    margin: "0 auto",
    padding: 28,
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    display: "flex",
    flexDirection: "column",
    fontFamily: "sans-serif",
    textAlign: "center",
  },
  cardWide: {
    maxWidth: 560,
  },
  title: {
    fontSize: 26,
    fontWeight: "bold",
    marginBottom: 18,
    color: "#111827",
  },
  text: {
    color: "#334155",
    fontSize: 15,
    lineHeight: 1.65,
    margin: "0 0 12px",
  },
  subtle: {
    color: "#64748b",
    fontSize: 13,
    marginTop: 6,
  },
  buttonRow: {
    marginTop: 22,
    display: "flex",
    justifyContent: "center",
  },
  primaryButton: {
    display: "inline-block",
    padding: "12px 18px",
    fontSize: 16,
    backgroundColor: "#1E2A78",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    textDecoration: "none",
    fontWeight: 700,
  },
};

export default ConnectRefresh;
